import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Check, Download, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { track } from "@/lib/analytics";
import { Reveal, Overline } from "@/components/Reveal";
import { useBookingUrl } from "@/context/ContentContext";

export default function DetoxGospel() {
  const [ebook, setEbook] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [optIn, setOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const bookingUrl = useBookingUrl();

  useSEO(
    "The Detox Gospel | Rooted Healing",
    "Request your free copy of The Detox Gospel by Staci Freed and explore a gentle, educational approach to holistic wellness."
  );

  useEffect(() => {
    track("ebook_landing_view");
    api.get("/content/ebook").then(({ data }) => setEbook(data)).catch(() => setEbook(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/ebook/request", { ...form, newsletter_opt_in: optIn });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not submit your request."));
    }
    setLoading(false);
  };

  const inputCls =
    "w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3.5 text-sm text-charcoal placeholder:text-taupe/70 outline-none transition-colors focus:border-gold";

  if (ebook === null) {
    return (
      <main className="flex min-h-screen items-center justify-center pt-[76px]">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </main>
    );
  }

  return (
    <main data-testid="ebook-page" className="pt-[76px]">
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
        {result ? (
          <Reveal>
            <div
              data-testid="ebook-confirmation"
              className="mx-auto max-w-2xl rounded-[2.5rem] border border-forest/10 bg-white/75 p-10 text-center shadow-[0_25px_60px_rgba(23,63,53,0.12)]"
            >
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage">
                <Check className="h-7 w-7 text-forest" />
              </span>
              <h1 className="mt-6 font-serif text-3xl text-forest sm:text-4xl">
                Your Detox Gospel Ebook Is Ready
              </h1>
              <p className="mt-4 leading-relaxed text-charcoal/70">
                {ebook.confirmation_message ||
                  "Thank you for your interest. Your request has been received."}
              </p>
              {result.download_url ? (
                <a
                  href={result.download_url}
                  data-testid="ebook-download-button"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep"
                >
                  <Download className="h-4 w-4" />
                  Download Ebook
                </a>
              ) : (
                <p className="mt-6 text-sm text-taupe">
                  Your copy will be available for download shortly.{" "}
                  {result.email_sent
                    ? "We also sent a download link to your email."
                    : "Check your email for your download link."}
                </p>
              )}
              {result.download_url && result.email_sent && (
                <p className="mt-4 text-xs text-taupe">
                  We also sent this link to your email so you can find it later.
                </p>
              )}
              <div className="mt-9 border-t border-forest/10 pt-8">
                <Sparkles className="mx-auto h-5 w-5 text-gold" />
                <h2 className="mt-3 font-serif text-xl text-forest">
                  Want to Explore Your Own Wellness Journey?
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-charcoal/70">
                  Discover patterns in your everyday experiences with Rooted Healing's
                  personalized Wellness Discovery.
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/wellness-discovery"
                    data-testid="ebook-discovery-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-forest-deep transition-colors hover:bg-gold-soft"
                  >
                    Explore My Wellness Discovery
                  </Link>
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ebook-consult-cta"
                    onClick={() => track("booking_click", { location: "ebook-confirmation" })}
                    className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-7 py-3.5 text-sm font-semibold text-forest transition-colors hover:border-gold"
                  >
                    Book a Consultation
                  </a>
                </div>
                <p className="mt-4 text-xs text-taupe">
                  Entirely optional. The ebook is yours either way.
                </p>
              </div>
            </div>
          </Reveal>
        ) : (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <Reveal className="text-center lg:text-left">
              <Overline>A Free Ebook from Rooted Healing</Overline>
              {ebook.cover_url && (
                <img
                  src={ebook.cover_url}
                  alt={`${ebook.title} ebook cover`}
                  className="mx-auto mt-8 w-64 rounded-2xl shadow-[0_25px_60px_rgba(23,63,53,0.2)] lg:mx-0"
                />
              )}
              <h1 className="mt-8 font-serif text-4xl leading-tight text-forest sm:text-5xl">
                {ebook.title}
              </h1>
              {ebook.subtitle && (
                <p className="mt-2 font-serif text-xl italic text-gold-dark">{ebook.subtitle}</p>
              )}
              <p className="mt-3 text-sm font-semibold uppercase tracking-[0.2em] text-taupe">
                by {ebook.author}
              </p>
              {ebook.description ? (
                <p className="mt-5 max-w-xl leading-relaxed text-charcoal/70 lg:pr-6">
                  {ebook.description}
                </p>
              ) : (
                <p className="mt-5 max-w-xl leading-relaxed text-charcoal/70 lg:pr-6">
                  A gentle, educational guide to Staci's approach to holistic wellness. Details
                  are being lovingly prepared.
                </p>
              )}
            </Reveal>

            <Reveal delay={0.1}>
              {!ebook.published ? (
                <div className="rounded-[2.5rem] border border-forest/10 bg-white/75 p-10 text-center shadow-[0_25px_60px_rgba(23,63,53,0.12)]">
                  <BookOpen className="mx-auto h-8 w-8 text-gold" />
                  <h2 className="mt-4 font-serif text-2xl text-forest">Coming Soon</h2>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                    The Detox Gospel is being prepared for release. Check back soon, or join the
                    newsletter below to hear when it is ready.
                  </p>
                </div>
              ) : (
                <form
                  data-testid="ebook-form"
                  onSubmit={submit}
                  className="rounded-[2.5rem] border border-forest/10 bg-white/75 p-8 shadow-[0_25px_60px_rgba(23,63,53,0.12)] sm:p-10"
                >
                  <h2 className="font-serif text-2xl text-forest">
                    Get Your Copy of The Detox Gospel
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal/65">
                    Enter your details below to receive your free copy and begin exploring
                    Staci's approach to holistic wellness.
                  </p>
                  <div className="mt-6 flex flex-col gap-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <input
                        data-testid="ebook-first-name"
                        required
                        placeholder="First name"
                        value={form.first_name}
                        onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                        className={inputCls}
                      />
                      <input
                        data-testid="ebook-last-name"
                        placeholder="Last name (optional)"
                        value={form.last_name}
                        onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                    <input
                      data-testid="ebook-email"
                      required
                      type="email"
                      placeholder="Email address"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className={inputCls}
                    />
                    <input
                      data-testid="ebook-phone"
                      type="tel"
                      placeholder="Phone number (optional)"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      className={inputCls}
                    />
                    <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-charcoal/75">
                      <input
                        type="checkbox"
                        data-testid="ebook-newsletter-optin"
                        checked={optIn}
                        onChange={(e) => setOptIn(e.target.checked)}
                        className="brand-check mt-1 h-4 w-4 shrink-0 cursor-pointer"
                      />
                      Yes, I'd like to receive Rooted Healing newsletters, educational resources,
                      and occasional updates from Staci. I can unsubscribe at any time.
                    </label>
                  </div>
                  <button
                    type="submit"
                    data-testid="ebook-submit"
                    disabled={loading}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
                    Get My Free Ebook
                  </button>
                  <p className="mt-4 text-xs leading-relaxed text-taupe">
                    Your details are used only to deliver the ebook and, if you opt in above, to
                    send the newsletter. Requesting the ebook never creates an account or adds
                    you to marketing emails. A phone number is never used for calls or texts
                    without separate consent. See our{" "}
                    <a href="/#privacy" className="underline underline-offset-4">
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        )}
      </section>
    </main>
  );
}
